import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { EASE_OUT, MATRIX_GREEN, MC_ROUTES } from '../constants';
import { MatrixSection } from '../ui-helpers';

export function MulticonnectionDiagram() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const youX = 45;
  const hop1X = 180;
  const hop2X = 370;
  const selectedX = 560;
  const serverX = 720;

  return (
    <div ref={ref} className='w-full'>
      <MatrixSection delay={0.1}>
        <div className='rounded-xl border p-5 md:p-6' style={{ backgroundColor: `${MATRIX_GREEN}06`, borderColor: `${MATRIX_GREEN}25`, boxShadow: `0 0 30px ${MATRIX_GREEN}08` }}>
          <div className='mb-4 flex items-center justify-between'>
            <div className='font-mono text-xs uppercase tracking-wider' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN }}>
              {'>'} MULTICONNECTION // SIMULTANEOUS ROUTES
            </div>
            <div className='rounded border px-3 py-1 font-mono text-xs font-bold' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN, borderColor: `${MATRIX_GREEN}40`, backgroundColor: `${MATRIX_GREEN}10` }}>
              ACTIVE ROUTES: 05
            </div>
          </div>

          <div className='relative w-full overflow-x-auto'>
            <div style={{ minWidth: 780 }}>
              <svg viewBox='0 0 780 360' className='w-full' preserveAspectRatio='xMidYMid meet' style={{ height: 'auto' }}>
                <defs>
                  <filter id='glow-mc-lp'>
                    <feGaussianBlur stdDeviation='2' result='blur' />
                    <feMerge>
                      <feMergeNode in='blur' />
                      <feMergeNode in='SourceGraphic' />
                    </feMerge>
                  </filter>
                </defs>

                {MC_ROUTES.map((route, i) => {
                  const points = [
                    { x: youX, y: 170 },
                    { x: hop1X, y: route.y },
                    { x: hop2X, y: route.y },
                    { x: selectedX, y: 170 },
                    { x: serverX, y: 170 },
                  ];
                  const pathD = points.map((p, pi) => `${pi === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

                  return (
                    <g key={`route-${i}`}>
                      <motion.path d={pathD} fill='none' stroke={MATRIX_GREEN} strokeWidth='1.2' strokeOpacity='0.35' initial={{ pathLength: 0, opacity: 0 }} animate={inView ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }} transition={{ duration: 0.8, delay: 0.2 + i * 0.1, ease: EASE_OUT }} />
                      {inView && (
                        <circle r='3' fill={MATRIX_GREEN} filter='url(#glow-mc-lp)' opacity='0.9'>
                          <animateMotion dur={['2s', '2.3s', '1.8s', '2.5s', '2.1s'][i]} repeatCount='indefinite' path={pathD} />
                        </circle>
                      )}
                    </g>
                  );
                })}

                {/* YOU node */}
                <motion.g initial={{ opacity: 0, scale: 0 }} animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
                  <rect x={youX - 16} y={170 - 12} width='32' height='24' rx='3' fill={`${MATRIX_GREEN}15`} stroke={MATRIX_GREEN} strokeWidth='1.5' />
                  <rect x={youX - 6} y={170 + 12} width='12' height='3' rx='1' fill={MATRIX_GREEN} fillOpacity='0.5' />
                  <circle cx={youX} cy={170 - 1} r='3' fill={MATRIX_GREEN} fillOpacity='0.6' />
                  <text x={youX} y={170 + 32} textAnchor='middle' fill={MATRIX_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
                    YOU
                  </text>
                  <text x={youX} y={170 + 44} textAnchor='middle' fill={`${MATRIX_GREEN}70`} fontSize='8' fontFamily='monospace'>
                    LOS ANGELES
                  </text>
                </motion.g>

                {/* Intermediate nodes */}
                {MC_ROUTES.map((route, i) => (
                  <motion.g key={`nodes-${i}`} initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}>
                    <circle cx={hop1X} cy={route.y} r='6' fill={`${MATRIX_GREEN}20`} stroke={MATRIX_GREEN} strokeWidth='1' />
                    <text x={hop1X} y={route.y - 12} textAnchor='middle' fill={`${MATRIX_GREEN}70`} fontSize='8' fontFamily='monospace'>
                      {route.hops[0]?.label}
                    </text>
                    <circle cx={hop2X} cy={route.y} r='6' fill={`${MATRIX_GREEN}20`} stroke={MATRIX_GREEN} strokeWidth='1' />
                    <text x={hop2X + 14} y={route.y + 4} textAnchor='start' fill={`${MATRIX_GREEN}90`} fontSize='8' fontFamily='monospace'>
                      {route.server}
                    </text>
                    <text x={hop2X} y={route.y - 12} textAnchor='middle' fill={`${MATRIX_GREEN}70`} fontSize='8' fontFamily='monospace'>
                      {route.hops[1]?.label}
                    </text>
                  </motion.g>
                ))}

                {/* Selected node */}
                <motion.g initial={{ opacity: 0, scale: 0 }} animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }} transition={{ duration: 0.4, delay: 0.6 }}>
                  <circle cx={selectedX} cy={170} r='14' fill={`${MATRIX_GREEN}25`} stroke={MATRIX_GREEN} strokeWidth='1.5' />
                  <motion.circle cx={selectedX} cy={170} r='20' fill='none' stroke={MATRIX_GREEN} strokeWidth='0.5' animate={{ r: [20, 28, 20], opacity: [0.4, 0, 0.4] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' as const }} />
                  <text x={selectedX} y={174} textAnchor='middle' fill={MATRIX_GREEN} fontSize='8' fontFamily='monospace' fontWeight='bold'>
                    WASH
                  </text>
                  <text x={selectedX} y={170 + 30} textAnchor='middle' fill={MATRIX_GREEN} fontSize='9' fontFamily='monospace' fontWeight='bold'>
                    WASHINGTON 10
                  </text>
                  <text x={selectedX} y={170 + 42} textAnchor='middle' fill={`${MATRIX_GREEN}60`} fontSize='7' fontFamily='monospace'>
                    [SELECTED]
                  </text>
                </motion.g>

                {/* Game Server node */}
                <motion.g initial={{ opacity: 0, scale: 0 }} animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }} transition={{ duration: 0.4, delay: 0.7 }}>
                  <rect x={serverX - 18} y={170 - 14} width='36' height='28' rx='3' fill={`${MATRIX_GREEN}15`} stroke={MATRIX_GREEN} strokeWidth='1.5' />
                  <line x1={serverX - 10} y1={170 - 6} x2={serverX + 10} y2={170 - 6} stroke={MATRIX_GREEN} strokeWidth='1' strokeOpacity='0.4' />
                  <line x1={serverX - 10} y1={170} x2={serverX + 10} y2={170} stroke={MATRIX_GREEN} strokeWidth='1' strokeOpacity='0.4' />
                  <line x1={serverX - 10} y1={170 + 6} x2={serverX + 10} y2={170 + 6} stroke={MATRIX_GREEN} strokeWidth='1' strokeOpacity='0.4' />
                  <circle cx={serverX + 8} cy={170 - 6} r='1.5' fill={MATRIX_GREEN} fillOpacity='0.8' />
                  <circle cx={serverX + 8} cy={170} r='1.5' fill={MATRIX_GREEN} fillOpacity='0.8' />
                  <motion.circle cx={serverX + 8} cy={170 + 6} r='1.5' fill={MATRIX_GREEN} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.2, repeat: Infinity }} />
                  <text x={serverX} y={170 + 30} textAnchor='middle' fill={MATRIX_GREEN} fontSize='9' fontFamily='monospace' fontWeight='bold'>
                    GAME
                  </text>
                  <text x={serverX} y={170 + 42} textAnchor='middle' fill={`${MATRIX_GREEN}70`} fontSize='8' fontFamily='monospace'>
                    SERVER
                  </text>
                </motion.g>
              </svg>
            </div>
          </div>

          <div className='mt-4 flex items-center justify-between'>
            <div className='font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: `${MATRIX_GREEN}80` }}>
              ACTIVE ROUTES: 5 | BEST ROUTE: WASHINGTON 10
            </div>
            <div className='font-mono text-xs font-bold' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN }}>
              PACKET_LOSS: 0% | REDUNDANCY: TOTAL
            </div>
          </div>
        </div>
      </MatrixSection>
    </div>
  );
}
